import { S } from './state.js';
import { Audio } from './audio.js';
import { checkAchievements, checkUnlocks } from './achievements.js';
import { BAL } from './config.js';
import { ITEMS, TIERS } from './data/items.js';
import { RELICS, RELIC_BY_KEY } from './data/relics.js';
import { bossTier } from './data/bosses.js';
import { SAVE_KEY, UPGRADES } from './data/progression.js';
import { ITEM_TAGS, PROC_KEYS, WEAPONS, WEAPON_IDX, offerWeightMul, validateData } from './data/weapons.js';
import { player } from './entities.js';
import { rnd } from './fx.js';
import { recomputeStats } from './stats.js';
// ---- Companions: a small optional ally chosen at the menu. Each helps just a little and is designed
// NOT to carry the run — flat/tiny effects, no scaling with your build's procs. ----
export const COMPANIONS = [
  { key: 'none',   name: 'None',        icon: '–',  color: '#8a93b5', desc: 'go solo' },
  { key: 'drone',  name: 'Spark Drone', icon: '🛰️', color: '#7fb6ff', desc: 'zaps nearby foes' },
  { key: 'sprite', name: 'Coin Sprite', icon: '🧚', color: '#ffd66e', desc: 'widens coin pickup' },
  { key: 'mender', name: 'Mender',      icon: '➕', color: '#7be08a', desc: 'slowly heals you' },
  { key: 'bit',    name: 'Guard Bit',   icon: '🛡', color: '#b8c0e0', desc: 'rare emergency shield' },
];
export const COMPANION_IDX = {}; COMPANIONS.forEach((c, i) => COMPANION_IDX[c.key] = i);
S.weapon = 0;   // index into WEAPONS for the current run

S.items = {};  // itemKey -> count for the CURRENT run only (cleared each run)
S.equipment = [];   // UNBOUNDED gear collection (RoR-style) — keeps growing all run
S.mods = {};          // active unique gear modifiers (autograpple, splitshot, ...)
S.damageTakenMult = 1; // raised by risky uniques like Overdrive
export function totalItems() { let n = 0; for (const k in S.items) n += S.items[k]; return n; }
export function distinctProcItems() { let n = 0; for (const k of PROC_KEYS) if (S.items[k]) n++; return n; }
export function rollTier() {
  // level-up Luck biases drops toward richer tiers (weight × (1 + luck·tierIndex)); mythic stays weight 0
  const luck = S.luck || 0, keys = Object.keys(TIERS);
  let tot = 0; const w = keys.map((t, i) => { const v = Math.max(0, TIERS[t].weight * (1 + luck * i)); tot += v; return v; });
  let r = rnd() * tot;
  for (let i = 0; i < keys.length; i++) { if (r < w[i]) return keys[i]; r -= w[i]; }
  return 'common';
}
// 0 = old uniform item odds (kinetic floods the pool) … 1 = every archetype offered equally.
// 0.75 strongly evens out fire/poison/frost/storm/crit while leaving kinetic a little more common.
export const ARCH_OFFER_BIAS = 0.75;
export function randomItemOfTier(tier) {
  const pool = ITEMS.filter(i => i.tier === tier);
  // Equalize archetype representation: an archetype's offer odds shouldn't depend on how
  // many items it happens to own (kinetic has 13, poison 2). Each tagged archetype shares
  // ONE unit of weight across its items in this tier; untagged "glue" items keep weight 1.
  // ARCH_OFFER_BIAS softens the correction toward uniform (1 = full equalization, 0 = old behaviour).
  const tagCount = {};
  for (const i of pool) { const t = ITEM_TAGS[i.key]; if (t) tagCount[t] = (tagCount[t] || 0) + 1; }
  let total = 0;
  // weapon-evo guard (0.19, owner TODO): the offer system leans toward your DOMINANT build and
  // ownedDecay leans away from what you own — an unlucky early game could starve the held weapon's
  // evolution archetype out of the pool ("the game 'helping' your current build locks you out").
  // Until the weapon evolves, its resonant archetype keeps a foot in the door.
  const _wd = WEAPONS[S.weapon], _evoArch = (!S.EVO && _wd && _wd.evo) ? _wd.evo.arch : null;
  const wts = pool.map(i => {
    const t = ITEM_TAGS[i.key];
    const equal = t ? 1 / tagCount[t] : 1;                 // weight that makes every archetype equal
    // ×0.25 for items INERT in the current build (no lightning for Capacitor…); capped or
    // weapon-dead items are weight 0 — hidden outright (0.19, see offerWeightMul).
    // ownedDecay (0.17): each copy you ALREADY hold compounds the weight down (×0.65/copy) — stacking
    // deep stays possible, but the pool stops parroting your inventory back at you.
    const w = ((1 - ARCH_OFFER_BIAS) + ARCH_OFFER_BIAS * equal) * offerWeightMul(i.key)
            * Math.pow(BAL.offer.ownedDecay, S.items[i.key] || 0)
            * (t && t === _evoArch ? BAL.offer.evoGuard : 1)
            * (i.weight || 1);   // per-item base-rate nudge (e.g. Scholar entry items punch above their small roster)
    total += w; return w;
  });
  if (total <= 0) return pool[Math.floor(rnd() * pool.length)];   // whole tier hidden (capped/dead) — degenerate, but never crash
  let r = rnd() * total;
  for (let i = 0; i < pool.length; i++) { if (r < wts[i]) return pool[i]; r -= wts[i]; }
  return pool[pool.length - 1];
}
// a mythic you don't own yet — null when you've collected them all (sources fall back gracefully)
export function randomMythic() {
  const pool = ITEMS.filter(i => i.tier === 'mythic' && !S.items[i.key]);
  return pool.length ? pool[Math.floor(rnd() * pool.length)] : null;
}
// grant ONE copy of an item mid-run — the canonical path: stack it, recompute, credit any max-HP
// gain as healing (and clamp down if max HP SHRANK, e.g. Berserker), hand defensive picks their
// charge immediately. Was copy-pasted at 6 sites with drifting details (some forgot the shield charge).
export function grantItem(key) {
  const oldMax = S.MAX_HP;
  S.items[key] = (S.items[key] || 0) + 1;
  recomputeStats();
  player.hp = Math.min(S.MAX_HP, player.hp + Math.max(0, S.MAX_HP - oldMax));
  if (key === 'shield' || key === 'bastion') player.shield = Math.min(S.MAX_SHIELD, player.shield + 1);
}
export function shrineItemPrice(it) {   // per-item price: scales with items owned, the WAVE, and rarity
  // wave term added in 0.10: income scales all run (coin drops × greed × fervor) but sinks didn't —
  // 0.9 telemetry showed players banking ~5-10k while spending ~600 mid-run (<7% of income)
  return Math.round((18 + totalItems() * 10 + S.waveNum * 5) * TIERS[it.tier].mult * Math.pow(0.8, S.items.nightmarket || 0)
                    * Math.max(0.6, 1 - 0.08 * (S.items.hagglers || 0)));   // Night Market: everything's cheaper after dark; Haggler's Charm talks them down (cap −40%)
}
// Item Cache stock (0.11): item shrines hold THREE items and open a buy-1-of-3 draft — coins now buy a
// DECISION, not a random walk-over pickup. The third slot leans toward your leading archetype (committed
// or budding) so spending can deepen a build instead of diluting it.
export function rollShrineChoices() {
  const picks = [randomItemOfTier(rollTier())];
  // slot 2 (0.19, anti-lockout): while the held weapon is un-evolved, half of all caches lean this
  // slot toward ITS archetype — there is always a road back to your gun's evolution, no matter how
  // the early game rolled or how hard slot 3 leans into your current build.
  const _wd2 = WEAPONS[S.weapon];
  if (!S.EVO && _wd2 && _wd2.evo && rnd() < 0.5) {
    const t2 = rollTier();
    const onEvo = ITEMS.filter(i => i.tier === t2 && ITEM_TAGS[i.key] === _wd2.evo.arch && offerWeightMul(i.key) > 0);
    picks.push(onEvo.length ? onEvo[Math.floor(rnd() * onEvo.length)] : randomItemOfTier(t2));
  } else picks.push(randomItemOfTier(rollTier()));
  let tag = S.buildTag;
  if (!tag) { let bv = 0; for (const t in S.arch) if (S.arch[t] > bv) { bv = S.arch[t]; tag = t; } if (!bv) tag = null; }
  const tier = rollTier();
  const onTheme = tag ? ITEMS.filter(i => i.tier === tier && ITEM_TAGS[i.key] === tag && offerWeightMul(i.key) > 0) : [];   // theme slot respects the hide rules too (0.19)
  picks.push(onTheme.length && rnd() < 0.6 ? onTheme[Math.floor(rnd() * onTheme.length)] : randomItemOfTier(tier));
  return picks;
}
// Blood Shrines deal in HP, not coins — biased toward the good tiers (the god-run gamble)
export function rollBloodTier() { const r = rnd(); return r < 0.20 ? 'legendary' : r < 0.52 ? 'epic' : r < 0.72 ? 'lunar' : 'uncommon'; }
export function bloodCost() { return Math.max(8, Math.round((S.MAX_HP + S.hpPenalty) * S.BLOOD_COST_PCT)); }
// non-item shrine kinds (gamble / build re-roll) — accent color, glyph & banner used by drawShrines + minimap
export const SHRINE_STYLE = {
  blood:   { col: '#ff4a5a', icon: '🩸', tag: '☠ BLOOD' },
  reroll:  { col: '#c9a3ff', icon: '🎲', tag: '🎲 GAMBLE' },
  pandora: { col: '#ffd24a', icon: '🎁', tag: 'PANDORA' },
  vault:   { col: '#8affec', icon: '🗝️', tag: '🗝️ VAULT' },   // once per run, far out near the wall: one MYTHIC, priced steep
};
export function vaultPrice() { return BAL.mythic.vaultBase + S.waveNum * BAL.mythic.vaultPerWave; }

S.save = { money: 0, best: 0, upgrades: {} };
export function loadSave() {
  try { const raw = localStorage.getItem(SAVE_KEY); if (raw) S.save = JSON.parse(raw); } catch (e) {}
  if (!S.save.upgrades) S.save.upgrades = {};
  for (const u of UPGRADES) if (typeof S.save.upgrades[u.key] !== 'number') S.save.upgrades[u.key] = 0;
  if (typeof S.save.money !== 'number') S.save.money = 0;
  if (typeof S.save.best !== 'number') S.save.best = 0;
  if (typeof S.save.kills !== 'number') S.save.kills = 0;          // lifetime kills
  if (typeof S.save.coinsTotal !== 'number') S.save.coinsTotal = 0;// lifetime coins banked
  if (typeof S.save.runs !== 'number') S.save.runs = 0;
  if (!S.save.unlocks) S.save.unlocks = {};
  if (!S.save.achievements) S.save.achievements = {};
  if (typeof S.save.startWeapon !== 'string' || WEAPON_IDX[S.save.startWeapon] == null) S.save.startWeapon = 'blaster';
  if (typeof S.save.companion !== 'string' || COMPANION_IDX[S.save.companion] == null) S.save.companion = 'none';
  if (!S.save.settings) {   // default "reduce flashing" ON if the OS asks for reduced motion
    let rm = false; try { rm = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) {}
    S.save.settings = { reduceFlash: rm, shake: true };
  }
  if (typeof S.save.settings.reduceFlash !== 'boolean') S.save.settings.reduceFlash = false;
  if (typeof S.save.settings.shake !== 'boolean') S.save.settings.shake = true;
  if (typeof S.save.settings.dmgMeter !== 'boolean') S.save.settings.dmgMeter = true;   // live damage meter (toggle: K)
  if (typeof S.save.settings.autoAim !== 'boolean') S.save.settings.autoAim = false;       // 0.18: auto-aim + auto-fire at the nearest foe (toggle: T; forced ON the first time a touch screen is detected)
  if (typeof S.save.settings.autoShop !== 'boolean') S.save.settings.autoShop = false;     // 0.18: shops / caches / level-ups / boss drafts resolve themselves (pause-menu toggle only — no key, it spends coins)
  if (typeof S.save.settings.statsPanel !== 'boolean') S.save.settings.statsPanel = false; // 0.18: live derived-stats panel (toggle: B)
  if (!Array.isArray(S.save.scores)) S.save.scores = [];   // local leaderboard (top runs on this device)
  if (typeof S.save.name !== 'string' || !S.save.name) S.save.name = 'Player';   // name for the global board
  // 0.25 meta-progression (additive — old saves load with an empty collection / 1 slot / Lv1)
  if (typeof S.save.accountXp !== 'number') S.save.accountXp = 0;          // lifetime meta-XP (account level is derived)
  if (typeof S.save.timeTotal !== 'number') S.save.timeTotal = 0;          // lifetime survival seconds
  if (!S.save.relicsOwned || typeof S.save.relicsOwned !== 'object') S.save.relicsOwned = {};   // permanent relic collection: key→star level (1..3)
  for (const k in S.save.relicsOwned) {   // 0.26 fusion migration: legacy key→true becomes key→1★; clamp to 1..3; drop keys for removed relics
    if (!RELIC_BY_KEY[k]) { delete S.save.relicsOwned[k]; continue; }
    const v = S.save.relicsOwned[k];
    S.save.relicsOwned[k] = (typeof v === 'number' && v >= 1) ? Math.min(3, Math.floor(v)) : 1;
  }
  if (!Array.isArray(S.save.relicLoadout)) S.save.relicLoadout = [];       // equipped relic keys (length ≤ relicSlots)
  S.save.relicLoadout = S.save.relicLoadout.filter((k, i, a) => RELIC_BY_KEY[k] && a.indexOf(k) === i);   // drop unknown/dup keys (safe vs a removed relic)
  checkUnlocks(true);   // backfill any milestones an old save already met (silent)
  checkAchievements(true);   // backfill lifetime achievements an old save already earned (silent)
}
export function persist() { try { localStorage.setItem(SAVE_KEY, JSON.stringify(S.save)); } catch (e) {} }

// ---- RELIC DROPS (0.25): a boss kill can yield a relic into the PERMANENT collection. Deeper waves
// (via bossTier) raise both the drop chance and the rarity ceiling. Dupes refund meta-XP (never wasted). ----
export function rollRelicTier(wave) {
  const t = bossTier(wave);   // 0..3 — reuse the boss-tier depth curve so relic rarity tracks the bosses
  const w = { common: Math.max(10, 60 - t * 15), rare: 30 + t * 5, exotic: 5 + t * 12 };
  let r = rnd() * (w.common + w.rare + w.exotic);
  if ((r -= w.common) < 0) return 'common';
  if ((r -= w.rare) < 0) return 'rare';
  return 'exotic';
}
function rollRelicOfTier(tier) {
  const owned = S.save.relicsOwned || {};
  let pool = RELICS.filter(x => x.tier === tier && !owned[x.key]);   // prefer a relic you DON'T own yet
  if (!pool.length) pool = RELICS.filter(x => x.tier === tier);      // whole tier owned → a dupe (refunds XP)
  if (!pool.length) pool = RELICS;                                   // defensive
  return pool[Math.floor(rnd() * pool.length)];
}
export function grantRelic(relic) {
  if (!relic) return;
  if (!S.save.relicsOwned) S.save.relicsOwned = {};
  if (!S.relicToasts) S.relicToasts = [];
  const cur = S.save.relicsOwned[relic.key] || 0;
  if (cur >= 3) {   // already MAXED (★★★) → meta-XP refund (scaled by tier) so a drop is never dead
    const refund = relic.tier === 'exotic' ? 800 : relic.tier === 'rare' ? 400 : 200;
    S.save.accountXp = (S.save.accountXp || 0) + refund;
    S.relicToasts.push({ relic, dupe: true, maxed: true, level: 3, refund, life: 220, life0: 220 });
  } else if (cur >= 1) {   // DUPE of an owned relic → FUSE it up a star (its upside scales; downside holds)
    const level = cur + 1;
    S.save.relicsOwned[relic.key] = level;
    S.relicToasts.push({ relic, upgrade: true, level, life: 240, life0: 240 });
    Audio.play('powerup');
  } else {   // first copy
    S.save.relicsOwned[relic.key] = 1;
    S.relicToasts.push({ relic, dupe: false, level: 1, life: 260, life0: 260 });
    Audio.play('powerup');
  }
  persist();
}
export function maybeDropRelic(wave) {
  const chance = Math.min(0.92, 0.5 + 0.12 * bossTier(wave));   // ~50% at the first boss → ~86-92% deep
  if (rnd() >= chance) return null;
  const relic = rollRelicOfTier(rollRelicTier(wave));
  grantRelic(relic);
  return relic;
}
